/**
 * Where a V2 order is in its life (orders-db `orders.stage`): received →
 * admitted → routed → at_provider → shipped → delivered, then returning /
 * returned, or canceled at any point. Finer than the tab: Pending Fulfillment
 * holds both `routed` and `at_provider`. Legacy orders leave `stage` off.
 */
import type { OrderStage } from '@skupreme/api-contracts/core';
import type { BadgeTone } from '@skupreme/shared/ui';

type StageInfo = { label: string; tone: BadgeTone; hint: string };

const STAGES: Record<OrderStage, StageInfo> = {
  received: {
    label: 'Received',
    tone: 'neutral',
    hint: 'Imported from the channel, not checked yet.',
  },
  admitted: {
    label: 'Admitted',
    tone: 'neutral',
    hint: 'Checked and waiting for a provider to be chosen.',
  },
  routed: {
    label: 'Routed',
    tone: 'info',
    hint: 'A provider is chosen; it has not accepted the order yet.',
  },
  at_provider: {
    label: 'At provider',
    tone: 'info',
    hint: 'The provider accepted the order and is fulfilling it.',
  },
  shipped: {
    label: 'Shipped',
    tone: 'accent',
    hint: 'Handed to the carrier.',
  },
  delivered: {
    label: 'Delivered',
    tone: 'success',
    hint: 'The carrier reported it delivered.',
  },
  returning: {
    label: 'Returning',
    tone: 'caution',
    hint: 'A return is on its way back.',
  },
  returned: {
    label: 'Returned',
    tone: 'caution',
    hint: 'The return has arrived.',
  },
  canceled: {
    label: 'Canceled',
    tone: 'danger',
    hint: 'Canceled; nothing more will ship.',
  },
};

export type StageOrder = { stage?: OrderStage | string | null };

/*
 * The stage nearly every order of a tab is at. A row only shows its stage when
 * it is another one: on Pending Fulfillment, `routed` among `at_provider`.
 */
const USUAL_STAGE: Record<string, OrderStage> = {
  'Pending Shipment': 'admitted',
  /* EN-4403: In Progress holds orders with a label of ours, stored shipped. */
  'Pending Fulfillment': 'shipped',
  Shipped: 'shipped',
  Returns: 'returned',
  Canceled: 'canceled',
  held: 'admitted',
};

/*
 * Never a badge: Routed only repeats the shipment card's Fulfillment fact (the
 * provider and who chose it), and nearly every Unfulfilled V2 row is routed.
 */
const UNSHOWN_STAGES: ReadonlySet<OrderStage> = new Set(['routed']);

/** Whether the stage is ever shown as a badge. */
export function isStageShown(stage: OrderStage): boolean {
  return !UNSHOWN_STAGES.has(stage);
}

/** Whether a row on this tab should show its stage. */
export function showsStageOn(
  tab: string | undefined,
  order: StageOrder | null | undefined,
): boolean {
  const stage = getStage(order);

  return (
    !!stage &&
    isStageShown(stage.stage) &&
    (!tab || USUAL_STAGE[tab] !== stage.stage)
  );
}

/** The order's stage with its words, or null for a legacy order or an unknown value. */
export function getStage(
  order: StageOrder | null | undefined,
): (StageInfo & { stage: OrderStage }) | null {
  const stage = order?.stage;

  if (!stage || !Object.hasOwn(STAGES, stage)) {
    return null;
  }

  return { stage: stage as OrderStage, ...STAGES[stage as OrderStage] };
}
